import React, { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';

const DEFAULT_SIZE = 32;

const leadingTrimFix = css`
    margin-top: -0.2em;
`;

interface AvatarProps {
    size?: number;
    color?: string;
    backgroundColor?: string;
}

export const Avatar = (props: PropsWithChildren<AvatarProps>) => {
    const { children, size = DEFAULT_SIZE, backgroundColor, color } = props;

    return (
        <AvatarCircle $backgroundColor={backgroundColor} $color={color} $size={size}>
            <CircleAvatarContent>{children}</CircleAvatarContent>
        </AvatarCircle>
    );
};

const AvatarCircle = styled.div<{ $size: number; $backgroundColor?: string; $color?: string }>(
    (props) => `
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    height: ${props.$size}px;
    width: ${props.$size}px;
    box-sizing: border-box;
    border-radius: 50%;
    overflow: hidden;
    border: 1px solid ${props?.color ?? props.theme.colors.cocoa900};
    color: ${props?.color ?? props.theme.colors.cocoa900};
    background-color: ${props?.$backgroundColor ? props.$backgroundColor : 'transparent'};
`,
);

const CircleAvatarContent = styled.div`
    ${leadingTrimFix}
`;

export const DiamondAvatar = (props: PropsWithChildren<AvatarProps>) => {
    const { children, size = DEFAULT_SIZE, color, backgroundColor } = props;

    return (
        <DiamondAvatarContainer $size={size}>
            <DiamondBorder $backgroundColor={backgroundColor} $color={color} />
            <DiamondAvatarContent>{children}</DiamondAvatarContent>
        </DiamondAvatarContainer>
    );
};

const DiamondAvatarContainer = styled.div<{ $size: number; $color?: string }>(
    (props) => `
    position: relative;
    flex-shrink: 0;
    height: ${props.$size}px;
    width: ${props.$size}px;
    color: ${props?.color ?? props.theme.colors.cocoa900};
    display: flex;
    justify-content: center;
    align-items: center;
`,
);

const DiamondBorder = styled.div<{ $color?: string; $backgroundColor?: string }>`
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    border-radius: 2px;
    transform: rotate(45deg);
    border: 1px solid ${(props) => props?.$color ?? props.theme.colors.cocoa900};
    background-color: ${(props) => (props?.$backgroundColor ? props.$backgroundColor : 'transparent')};
`;

const DiamondAvatarContent = styled.div`
    position: absolute;
    ${leadingTrimFix}
`;

export const TriangleAvatar = (props: PropsWithChildren<AvatarProps>) => {
    const { children, size = DEFAULT_SIZE, color, backgroundColor } = props;
    const strokeColor = color ?? '#2D3748'; // Default cocoa900 equivalent

    return (
        <TriangleAvatarContainer $size={size}>
            <svg width="40" height="40" viewBox="0 0 40 40" style={{ position: 'absolute' }}>
                <polygon
                    points="20,0 38,34 2,34"
                    fill={backgroundColor || 'transparent'}
                    stroke={strokeColor}
                    strokeWidth="1"
                />
            </svg>
            <TriangleAvatarContent $color={strokeColor}>{children}</TriangleAvatarContent>
        </TriangleAvatarContainer>
    );
};

const TriangleAvatarContainer = styled.div<{ $size: number; $color?: string }>(
    (props) => `
    position: relative;
    flex-shrink: 0;
    height: ${props.$size}px;
    width: ${props.$size}px;
    color: ${props?.color ?? props.theme.colors.cocoa900};
    display: flex;
    justify-content: center;
    align-items: center;
`,
);

const TriangleAvatarContent = styled.div<{ $color: string }>`
    position: absolute;
    margin-top: 4px;
    color: ${(props) => props.$color};
    ${leadingTrimFix}
`;
